import { useState, useEffect } from "react";
import Modal from "react-modal";
import api from "../utils/axiosInstance";
import "../styles/DeviceModal.css";

Modal.setAppElement("#root");

const DeviceModal = ({ isOpen, onClose, onSave, device, devices }) => {
    let initialFormData = {
        sku: "",
        name: "",
        description: "",
        available: false,
        published: false,
        published_at: null,
        image: null,
        instruction_url: "",
    };
    const [formData, setFormData] = useState(initialFormData);
    const [initialData, setInitialData] = useState(initialFormData);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [errors, setErrors] = useState({});
    const [imageError, setImageError] = useState("");

    const handleImageChange = (e) => {
        const file = e.target.files[0];

        if (!file) {
            setFormData({ ...formData, image: null });
            return;
        }

        const allowedTypes = ["image/png", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
            setImageError("Invalid file format. Only PNG and WEBP allowed.");
            return;
        }

        if (file.size > 1024 * 1024) {
            setImageError("File size must not exceed 1 MB.");
            return;
        }

        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
            const { width, height } = img;

            const validWidth = width >= 854 && width <= 940;
            const validHeight = height >= 480 && height <= 530;

            if (!validWidth || !validHeight) {
                setImageError("Image resolution must be from 854x480 to 940x530.");
                return;
            }

            setImageError("");
            setFormData({ ...formData, image: file });
        };
    };


    const validateForm = () => {
        const newErrors = {};

        if (!formData.sku) {
            newErrors.sku = "SKU is required";
        } else if (!/^[a-zA-Z0-9]{1,15}$/.test(formData.sku)) {
            newErrors.sku = "SKU must be up to 15 characters, letters and digits only";
        } else {
            const isDuplicate = devices.some(
                (d) =>
                    d.sku.toLowerCase() === formData.sku.toLowerCase() &&
                    d.id !== device?.id // разрешаем текущий id
            );
            if (isDuplicate) {
                newErrors.sku = "SKU must be unique";
            }
        }

        if (!formData.name) {
            newErrors.name = "Name is required";
        } else if (!/^[a-zA-Z0-9 ]{1,100}$/.test(formData.name)) {
            newErrors.name = "Name must be up to 100 characters, letters and digits only";
        }

        if (!formData.description) {
            newErrors.description = "Description is required";
        } else if (formData.description.length > 1000) {
            newErrors.description = "Description must be up to 1000 characters";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    useEffect(() => {
        if (device) {
            const formatted = {
                sku: device.sku,
                name: device.name,
                description: device.description,
                image: null,
                available: device.available,
                published: device.published,
                instruction_url: device.instruction_url || "",
                published_at: device.published_at
                    ? new Date(device.published_at).toISOString().slice(0, 16)
                    : "",
            };

            setFormData(formatted);
            setInitialData(formatted);
        } else {
            setFormData(initialFormData);
            setInitialData(initialFormData);
        }
    }, [device]);

    const isChanged = JSON.stringify({ ...formData, image: null }) !== JSON.stringify({ ...initialData, image: null });

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};

        if (!validateForm()) return;

        if (!formData.image && !device?.id) {
            newErrors.image = "Image is required for new devices";
            setErrors(newErrors);
            return;
        }

        try {
            const form = new FormData();
            form.append("sku", formData.sku);
            form.append("name", formData.name);
            form.append("description", formData.description);
            form.append("available", formData.available);
            form.append("published", formData.published);
            form.append("published_at", formData.published_at);
            form.append("instruction_url", formData.instruction_url);
            if (formData.image) {
                form.append("image", formData.image);
            }

            if (device && device.id) {
                await api.post(`/devices/${device.id}/update`, form, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            } else {
                await api.post("/devices", form, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }

            onSave();
            onClose();
        } catch (err) {
            console.error("Ошибка при сохранении устройства", err);
        }
    };

    const handleAvailableChange = (e) => {
        const newAvailable = e.target.checked;

        if (!newAvailable && formData.published) {
            // нельзя выключить available, если включен published
            setErrorMessage("To publish a device, its Availability must be enabled.");
            setShowErrorModal(true);
            return;
        }

        setFormData((prev) => ({ ...prev, available: newAvailable }));
    };

    const handlePublishedChange = (e) => {
        const newPublished = e.target.checked;

        if (newPublished && !formData.available) {
            // если включаем published, но available выключен — включаем его автоматически
            setFormData((prev) => ({
                ...prev,
                published: true,
                available: true,
            }));
            return;
        }

        if (!newPublished) {
            // отключаем published и сбрасываем дату
            setFormData((prev) => ({
                ...prev,
                published: false,
                published_at: "",
            }));
            return;
        }

        setFormData((prev) => ({ ...prev, published: true }));
    };


    return (
        <Modal isOpen={isOpen} onRequestClose={onClose} className="device-modal-content" overlayClassName="device-modal-overlay">
            <button className="device-modal-close" onClick={onClose}>×</button>
            <h2>{device ? "Edit device" : "Add device"}</h2>
            <form onSubmit={handleSubmit} className="device-modal-form">
                <label>SKU</label>
                <input
                    type="text"
                    value={formData.sku || ""}
                    onChange={(e) => setFormData({...formData, sku: e.target.value})}
                    required
                />
                {errors.sku && <div className="form-error">{errors.sku}</div>}

                <label>Name</label>
                <input
                    type="text"
                    value={formData.name || ""}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                />
                {errors.name && <div className="form-error">{errors.name}</div>}

                <label>Description</label>
                <textarea
                    value={formData.description || ""}
                    maxLength={1000}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                />
                {errors.description && <div className="form-error">{errors.description}</div>}

                <label>Instruction URL</label>
                <textarea
                    value={formData.instruction_url || ""}
                    maxLength={600}
                    onChange={(e) =>
                        setFormData({...formData, instruction_url: e.target.value})
                    }
                />
                <div className="field-description">
                    Up to 600 characters
                </div>


                <label>Image</label>
                <input type="file" accept=".png,.webp" onChange={handleImageChange}/>
                <div className="field-description">
                    Image resolution should be from 854x480 to 940x530. <br/>
                    The size of the uploaded file must not exceed 1 Mb. <br/>
                    The file extension must be png or webp.
                </div>
                {imageError && <div className="form-error">{imageError}</div>}
                {errors.image && <div className="form-error">{errors.image}</div>}

                <div className="form-group">
                    <span>Available</span>
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={formData.available}
                            onChange={handleAvailableChange}
                        />
                        <span className="slider"></span>
                    </label>
                    <div className="field-description">
                        To connect the device to the app
                    </div>
                </div>

                <div className="form-group">
                    <span>Publish status</span>
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={formData.published}
                            onChange={handlePublishedChange}
                        />
                        <span className="slider"></span>
                    </label>
                    <div className="field-description">
                        To display the device in the device catalog
                    </div>
                </div>

                <div className="form-group">
                    <span>Publish date</span>
                    <input
                        type="datetime-local"
                        value={formData.published_at || ""}
                        onChange={(e) => setFormData({...formData, published_at: e.target.value})}
                        disabled={!formData.published}
                        style={{gridColumn: "1 / -1"}}
                    />
                </div>

                {showErrorModal && (
                    <div className="device-modal-error-overlay">
                        <div className="device-modal-error-box">
                            <p>{errorMessage}</p>
                            <button onClick={() => setShowErrorModal(false)}>OK</button>
                        </div>
                    </div>
                )}

                <button type="submit" disabled={!isChanged} className="device-modal-button">
                    {device ? "Save changes" : "Add device"}
                </button>
            </form>
        </Modal>
    );
};

export default DeviceModal;
